import { Session, User } from "@supabase/supabase-js";
import { useContext, useState, useEffect, createContext } from "react";
import { supabase } from "../config/supabaseClient";

// create a context for authentication
const AuthContext = createContext<{
	isOnboardingFinished: boolean;
	username: string | null;
	session: Session | null;
	user: User | null;
	signOut: () => void;
	currentOnboardingStep: number;
	isAdmin?: boolean;
	adminId?: string | null;
	isMember?: boolean;
	memberId?: string | null;
}>({
	isOnboardingFinished: false,
	username: null,
	session: null,
	user: null,
	signOut: () => { },
	currentOnboardingStep: 1,
	isAdmin: false,
	adminId: null,
	isMember: false,
	memberId: null,
});

export const AuthProvider = ({ children }: any) => {
	const [username, setUsername] = useState<string | null>(null);
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);
	const [currentOnboardingStep, setCurrentOnboardingStep] = useState(1);
	const [isOnboardingFinished, setIsOnboardingFinished] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [adminId, setAdminId] = useState<string | null>(null);
	const [isMember, setIsMember] = useState(false);
	const [memberId, setMemberId] = useState<string | null>(null);
	useEffect(() => {
		// Check if admin key exists in localStorage
		const checkLocalStorage = () => {
			const adminDataString = localStorage.getItem("admin");
			const memberDataString = localStorage.getItem("member");
			const loggedInUsername = localStorage.getItem("loggedIn");

			if (adminDataString && loggedInUsername) {  // Ensure we have both pieces of data
				try {
					// Parse the JSON string to get the admin user object
					const adminUserData = JSON.parse(adminDataString);

					if (!adminUserData.user_id || !adminUserData.email) {
						console.error("Incomplete admin data detected");
						return false;
					}

					// Set the admin data
					setIsAdmin(true);
					setAdminId(adminUserData.user_id);
					setUsername(loggedInUsername);

					// Set the user object with the admin data
					setUser({
						id: adminUserData.user_id,
						email: adminUserData.email || '',
						user_metadata: {
							username: loggedInUsername,
							...adminUserData
						},
						app_metadata: {},
						aud: 'authenticated',
						created_at: '',
						name: adminUserData.name
					} as User);

					setLoading(false);
					return true; // Indicate we handled admin mode
				} catch (error) {
					console.error("Error parsing admin data:", error);
				}
			}

			if (memberDataString) {
				try {
					// Parse the JSON string to get the member user object
					const memberUserData = JSON.parse(memberDataString);

					// Set the user object with the member data
					setIsAdmin(false);
					setIsMember(true);
					setMemberId(memberUserData.user_id);
					setUsername(loggedInUsername);
					setUser({
						id: memberUserData.user_id,
						email: memberUserData.email || '',
						user_metadata: {
							username: loggedInUsername,
							...memberUserData
						},
						app_metadata: {},
						aud: 'authenticated',
						created_at: '',
						name: memberUserData.name
					} as User);

					// when refresh the page, the session is not updated, so we need to update the session
					supabase.auth.getSession().then(({ data: { session } }) => {
						setSession(session);
					});

					setLoading(false);
					return true; // Indicate we handled member mode
				} catch (error) {
					console.error("Error parsing member data:", error);
				}
			}

			// If we have loggedInUsername but no admin/member data, this is a regular user
			if (loggedInUsername && !adminDataString && !memberDataString) {
				setUsername(loggedInUsername);
				return false; // Let normal auth flow handle the rest
			}

			return false; // Indicate we didn't handle any special mode
		};

		// Initial check
		const handled = checkLocalStorage();

		// Set up event listeners for login events
		const handleMemberLogin = () => {
			checkLocalStorage();
		};

		const handleAdminLogin = () => {
			checkLocalStorage();
		};

		window.addEventListener('memberLogin', handleMemberLogin);
		window.addEventListener('adminLogin', handleAdminLogin);
		window.addEventListener('storage', checkLocalStorage);

		// Only proceed with normal auth flow if we didn't handle admin/member mode
		if (!handled) {
			// Normal authentication flow
			supabase.auth.getSession().then(({ data: { session } }) => {
				setSession(session);
				if (session?.user) {
					setUser(session.user);
					if (session.user.user_metadata) {
						setCurrentOnboardingStep(
							session.user.user_metadata?.currentOnboardingStep || 1
						);
						// Only set username if not already set by localStorage
						if (!username) {
							setUsername(session.user.user_metadata?.username || null);
						}
						setIsOnboardingFinished(
							session.user.user_metadata?.isOnboardingFinished || false
						);
					}
				}
				setLoading(false);
			});
		}

		const { data: listener } = supabase.auth.onAuthStateChange(
			(event, session) => {
				if (event === "SIGNED_OUT") {
					setUsername(null);
					setUser(null);
					setSession(null);
					setIsOnboardingFinished(false);
					setCurrentOnboardingStep(1);
					setIsAdmin(false);
					setIsMember(false);
					setAdminId(null);
					setMemberId(null);
					localStorage.removeItem("loggedIn");
					localStorage.removeItem("admin");
					localStorage.removeItem("member");
				} else if (!checkLocalStorage()) { // Only update if not in admin/member mode
					setSession(session);
					if (session?.user) {
						setUser(session.user);
						if (session.user.user_metadata) {
							setCurrentOnboardingStep(
								session.user.user_metadata?.currentOnboardingStep || 1
							);
							// Only set username if not already set by localStorage
							if (!username) {
								setUsername(session.user.user_metadata?.username || null);
							}
							setIsOnboardingFinished(
								session.user.user_metadata?.isOnboardingFinished || false
							);
						}
					}
				}
				setLoading(false);
			}
		);

		return () => {
			listener?.subscription.unsubscribe();
			window.removeEventListener('memberLogin', handleMemberLogin);
			window.removeEventListener('adminLogin', handleAdminLogin);
			window.removeEventListener('storage', checkLocalStorage);
		};
	}, []);

	const value = {
		isOnboardingFinished,
		username,
		session,
		user,
		signOut: () => supabase.auth.signOut(),
		currentOnboardingStep,
		isAdmin,
		adminId,
		isMember,
		memberId,
	};

	// use a provider to pass down the value
	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
		</AuthContext.Provider>
	);
};

// export the useAuth hook
export const useAuth = () => {
	return useContext(AuthContext);
};